import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { ShoppingBag, Heart, ClipboardList, ChevronLeft, ChevronRight, Store, Filter, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axiosInstance from '../api/axios';
import useCartStore from '../store/cartStore';
import useAuthStore from '../store/authStore';
import useWishlistStore from '../store/wishlistStore';
import useNotepadStore from '../store/notepadStore';
import AddToNotepadDialog from '../components/AddToNotepadDialog';
import { toast } from 'sonner';
import { imgVariant, imgSrcSet } from '../utils/imgUrl';
import { isNewProduct } from '../utils/productBadges';
import LimitedCollectionsSection from '../components/LimitedCollectionsSection';
import SaleBannerSection from '../components/SaleBannerSection';
import { formatEUR } from '../utils/format';

const Products = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [selectedSubcategory, setSelectedSubcategory] = useState(searchParams.get('sub') || '');
  const [activeCategory, setActiveCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  // Stable shuffle seed per visit so the "mixed" shop order doesn't change
  // every time React re-renders, but does change between visits.
  const [shopShuffleSeed] = useState(() => Math.floor(Math.random() * 100000) + 1);
  const categoryRefs = useRef({});

  // Build [{name, count, subs: [{name, count}]}] from current products list.
  const categoryGroups = useMemo(() => {
    const map = new Map();
    products.forEach((p) => {
      if (!p.category) return;
      if (!map.has(p.category)) map.set(p.category, { name: p.category, count: 0, subsMap: new Map() });
      const g = map.get(p.category);
      g.count += 1;
      if (p.subcategory) {
        g.subsMap.set(p.subcategory, (g.subsMap.get(p.subcategory) || 0) + 1);
      }
    });
    return Array.from(map.values()).map((g) => ({
      name: g.name,
      count: g.count,
      subs: Array.from(g.subsMap.entries())
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    })).sort((a, b) => a.name.localeCompare(b.name));
  }, [products]);

  const toggleGroup = (name) => {
    setExpandedGroups((prev) => ({ ...prev, [name]: !prev[name] }));
  };
  
  const addItem = useCartStore(state => state.addItem);
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const fetchWishlist = useWishlistStore(state => state.fetchWishlist);
  const toggleWishlist = useWishlistStore(state => state.toggleWishlist);
  const wishlistIds = useWishlistStore(state => state.wishlistIds);
  const checkIsInWishlist = useWishlistStore(state => state.isInWishlist);
  const fetchNotepad = useNotepadStore(state => state.fetchNotepad);
  const addToNotepad = useNotepadStore(state => state.addToNotepad);
  const notepadItems = useNotepadStore(state => state.notepadItems);
  const searchQuery = searchParams.get('search');
  
  const [notepadDialog, setNotepadDialog] = useState({ open: false, product: null });
  
  // Helper function to check if product is in wishlist
  const isInWishlist = (productId) => checkIsInWishlist(productId);
  
  // Helper function to check if product is in notepad
  const isInNotepad = (productId) => notepadItems.some(item => item.product_id === productId);
  
  // Sorting & Filtering State
  const [sortBy, setSortBy] = useState('default');
  const [priceRange, setPriceRange] = useState({ min: 0, max: 1000 });
  const [showOnlyAvailable, setShowOnlyAvailable] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      fetchWishlist();
      fetchNotepad();
    }
  }, [isAuthenticated, fetchWishlist, fetchNotepad]);

  useEffect(() => {
    fetchProductsAndCategories();
    // Update active category from URL parameter
    const categoryParam = searchParams.get('category');
    const subParam = searchParams.get('sub');
    if (categoryParam) {
      setActiveCategory(categoryParam);
      setSelectedCategory(categoryParam);
      setExpandedGroups((prev) => ({ ...prev, [categoryParam]: true }));
    } else {
      setSelectedCategory('');
    }
    if (subParam) setSelectedSubcategory(subParam);
    else setSelectedSubcategory('');
  }, [searchQuery, searchParams]);

  const fetchProductsAndCategories = async () => {
    setLoading(true);
    const url = '/products?include_vendor_info=true' + (searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : '');
    // 1× stiller Retry bei Netzwerkfehler — beugt „Fehler beim Laden" bei
    // transient Timeouts / Service-Worker-Renew vor.
    const tryFetch = async (attempt) => {
      try {
        const response = await axiosInstance.get(url, { timeout: 20000 });
        return response.data;
      } catch (err) {
        if (attempt === 0) {
          await new Promise((r) => setTimeout(r, 700));
          return tryFetch(1);
        }
        throw err;
      }
    };
    try {
      const allProducts = await tryFetch(0);
      const uniqueCategories = [...new Set(allProducts.map(p => p.category).filter(Boolean))];
      setProducts(allProducts);
      setCategories(uniqueCategories.sort());
      if (uniqueCategories.length > 0 && !activeCategory) {
        setActiveCategory(uniqueCategories[0]);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Fehler beim Laden der Produkte — bitte Seite neu laden');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product) => {
    const effectivePrice = (product.sale_price && product.sale_price < product.price) ? product.sale_price : null;
    if (!addItem(product, 1, null, effectivePrice)) return;
    toast.success(`${product.name} wurde zum Warenkorb hinzugefügt`);
  };

  const handleAddToWishlist = async (productId, e) => {
    e.stopPropagation();
    
    if (!isAuthenticated) {
      toast.error('Bitte melden Sie sich an, um Produkte zur Favoritenliste hinzuzufügen');
      navigate('/login');
      return;
    }

    const wasInWishlist = isInWishlist(productId);
    const success = await toggleWishlist(productId);
    
    if (success) {
      if (wasInWishlist) {
        toast.success('Von Favoritenliste entfernt');
      } else {
        toast.success('Zur Favoritenliste hinzugefügt');
      }
    } else {
      toast.error('Fehler beim Aktualisieren der Favoritenliste');
    }
  };

  const handleOpenNotepadDialog = (product, e) => {
    e.stopPropagation();
    
    if (!isAuthenticated) {
      toast.error('Bitte melden Sie sich an, um Produkte zum Merkzettel hinzuzufügen');
      navigate('/login');
      return;
    }

    setNotepadDialog({ open: true, product });
  };

  const handleAddToNotepadConfirm = async (quantity) => {
    const success = await addToNotepad(notepadDialog.product.id, quantity);
    
    if (success) {
      toast.success(`${quantity}x ${notepadDialog.product.name} zum Merkzettel hinzugefügt`);
    } else {
      toast.error('Fehler beim Hinzufügen zum Merkzettel');
    }
    
    setNotepadDialog({ open: false, product: null });
  };

  const scrollToCategory = (category) => {
    setActiveCategory(category);
    const element = categoryRefs.current[category];
    if (element) {
      const yOffset = -100;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const scrollContainer = (containerId, direction) => {
    const container = document.getElementById(containerId);
    if (container) {
      const scrollAmount = 400;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Apply sorting and filtering to products
  const getFilteredAndSortedProducts = (categoryProducts) => {
    let filtered = [...categoryProducts];
    
    // Filter by price range
    filtered = filtered.filter(p => p.price >= priceRange.min && p.price <= priceRange.max);
    
    // Filter by availability
    if (showOnlyAvailable) {
      filtered = filtered.filter(p => p.stock > 0);
    }
    
    // Sort products
    switch (sortBy) {
      case 'price_asc':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price_desc':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'name_asc':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name_desc':
        filtered.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 'newest':
        filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
      default:
        break;
    }
    
    return filtered;
  };

  const getProductsByCategory = (category) => {
    return products.filter(product => product.category === category);
  };

  const ProductCard = ({ product }) => (
    <div
      className="bg-white overflow-hidden border border-gray-200 hover:shadow-lg transition-shadow w-full h-full flex flex-col"
      data-testid={`product-${product.id}`}
    >
      <Link to={`/products/${product.id}`}>
        <div className="aspect-square relative overflow-hidden bg-[#FAF5EF] p-2">
          {/* Animated SALE sticker — wackelt sanft, fällt auf */}
          {product.sale_price && product.sale_price < product.price && (
            <div
              className="absolute top-2 left-2 z-10 flex items-center justify-center w-12 h-12 bg-[#B0512E] text-white font-bold text-xs shadow-lg sale-sticker-wobble"
              style={{
                clipPath: 'polygon(50% 0%, 90% 20%, 100% 60%, 75% 100%, 25% 100%, 0% 60%, 10% 20%)',
              }}
              title={`${Math.round((1 - product.sale_price / product.price) * 100)}% Rabatt`}
            >
              −{Math.round((1 - product.sale_price / product.price) * 100)}%
            </div>
          )}
          {/* „Neu"-Badge (14 Tage nach created_at) — automatisch, rechte obere Ecke */}
          {isNewProduct(product) && (
            <span
              className="absolute top-2 right-2 z-10 bg-[#5D4E3C] text-[#F5EDE4] text-[10px] font-bold uppercase tracking-widest px-2 py-1 shadow-md"
              data-testid={`product-new-badge-${product.id}`}
              title="In den letzten 14 Tagen neu eingetroffen"
            >
              Neu
            </span>
          )}
          {product.images && product.images.length > 0 ? (
            <img
              src={imgVariant(product.images[0], { w: 400, fmt: 'webp' })}
              srcSet={imgSrcSet(product.images[0], [200, 400, 600], 'webp')}
              sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 240px"
              alt={product.name}
              loading="lazy"
              decoding="async"
              fetchpriority="low"
              className="w-full h-full object-contain hover:scale-105 transition-transform duration-300 copy-protect-image"
              draggable="false"
            />
          ) : (
            <div className="w-full h-full bg-gray-200 flex items-center justify-center">
              <ShoppingBag className="h-12 w-12 text-gray-400" />
            </div>
          )}
        </div>
      </Link>
      
      {/* Content area: flex column, with action area pushed to bottom (mt-auto) */}
      <div className="p-4 flex flex-col flex-1 min-h-0">
        <Link to={`/products/${product.id}`} className="block">
          {/* Fixed min-height ensures buttons align across cards regardless of name length */}
          <h3 className="font-medium text-gray-900 mb-2 line-clamp-2 hover:text-blue-600 min-h-[2.8em] leading-snug">
            {product.name}
          </h3>
        </Link>
        
        {/* Vendor Badge */}
        {product.vendor_id && product.vendor_shop_name && (
          <Link 
            to={`/shop/${product.vendor_id}`}
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 text-xs bg-[#F5EDE4] text-[#8B7355] px-2 py-1 hover:bg-[#EBE0D4] transition-colors mb-2 self-start"
          >
            <Store className="h-3 w-3" />
            {product.vendor_shop_name}
          </Link>
        )}
        
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap min-h-[2rem]">
          {product.sale_price && product.sale_price < product.price ? (
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-lg font-bold text-white px-3 py-1 whitespace-nowrap" style={{ backgroundColor: '#B0512E' }}>
                {formatEUR(product.sale_price)}
              </span>
              <span className="text-xs text-[#8B7355] line-through whitespace-nowrap">
                {formatEUR(product.price)}
              </span>
              <span className="text-[10px] font-bold text-[#B0512E] px-1.5 py-0.5 bg-[#FFE4D6] border border-[#B0512E] whitespace-nowrap">
                −{Math.round((1 - product.sale_price / product.price) * 100)}%
              </span>
            </div>
          ) : (
            <span className="text-lg font-bold text-white px-3 py-1 whitespace-nowrap" style={{ backgroundColor: '#C4A084' }}>
              {formatEUR(product.price)}
              {product.price_type === 'per_quantity' && product.price_quantity > 1 && (
                <span className="text-xs font-normal ml-1">/ {product.price_quantity} St.</span>
              )}
            </span>
          )}
          {product.is_handmade && (
            <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 whitespace-nowrap">
              Handgemacht
            </span>
          )}
        </div>
        
        {/* Action area — pushed to the bottom so all cards align */}
        <div className="space-y-2 mt-auto">
          <Button
            onClick={() => handleAddToCart(product)}
            className="w-full text-xs"
            disabled={!(product.stock > 0)}
            size="sm"
          >
            {!(product.stock > 0) ? 'Ausverkauft' : 'In den Warenkorb'}
          </Button>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => handleAddToWishlist(product.id, e)}
              className={`flex-1 ${isInWishlist(product.id) ? 'bg-pink-50 border-pink-300' : ''}`}
              title="Zur Wishlist"
            >
              <Heart
                className={`h-4 w-4 ${isInWishlist(product.id) ? 'fill-pink-500 text-pink-500' : 'text-[#C4A084]'}`}
              />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => handleOpenNotepadDialog(product, e)}
              className={`flex-1 ${isInNotepad(product.id) ? 'bg-blue-50 border-blue-300' : ''}`}
              title="Zum Merkzettel"
            >
              <ClipboardList
                className={`h-4 w-4 ${isInNotepad(product.id) ? 'text-blue-500' : 'text-[#C4A084]'}`}
              />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#E8E0D5' }}>
        <div className="text-xl">Produkte werden geladen...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5EDE4', overflowX: 'hidden' }}>
      {/* Page Hero — brand-consistent header */}
      <div className="bg-white border-b" style={{ borderColor: '#E8DCC8' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <p
            className="text-[10px] sm:text-xs tracking-[4px] uppercase mb-2"
            style={{ color: '#C4A084' }}
          >
            {searchQuery ? 'Suchergebnisse' : 'Unsere Welt zum Stöbern'}
          </p>
          <h1
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#5D4E3C] leading-tight"
            data-testid="products-title"
            style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
          >
            {searchQuery ? `„${searchQuery}"` : 'Lieblingsstücke entdecken'}
          </h1>
          <div className="w-12 h-[2px] my-3" style={{ backgroundColor: '#C4A084' }} />
          <p className="text-sm sm:text-base text-[#6D5A47] max-w-2xl">
            {searchQuery
              ? 'Hier siehst du alle Treffer zu deiner Suche, schön nach Kategorien sortiert.'
              : 'Stöbere durch unsere Kategorien — vom Haargummi bis zum kleinen Schmuckstück, jede Auswahl mit Liebe kuratiert.'}
          </p>

          {/* SALE-Mini-Banner direkt im Shop-Header (rendert nur wenn aktiv) */}
          <div className="mt-4">
            <SaleBannerSection compact />
          </div>
        </div>

        {/* Compact category tiles (square) — works on all viewports */}
        {categories.length > 0 && (
          <div className="border-t" style={{ borderColor: '#E8DCC8' }} data-testid="category-tiles">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
              <div
                className="flex gap-3 sm:gap-4 overflow-x-auto pb-1"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
              >
                {categories.map((category) => {
                  const catProducts = getProductsByCategory(category);
                  const cover = catProducts.find(p => p.images?.[0])?.images?.[0];
                  const isActive = activeCategory === category;
                  return (
                    <button
                      key={category}
                      type="button"
                      onClick={() => scrollToCategory(category)}
                      data-testid={`category-tile-${category}`}
                      className="flex-shrink-0 flex flex-col items-center gap-1.5 group"
                      style={{ width: '76px' }}
                    >
                      <div
                        className="relative w-[68px] h-[68px] sm:w-[76px] sm:h-[76px] overflow-hidden transition-all"
                        style={{
                          border: `2px solid ${isActive ? '#5D4E3C' : '#E8DCC8'}`,
                          boxShadow: isActive ? '0 4px 8px rgba(93,78,61,0.15)' : 'none',
                        }}
                      >
                        {cover ? (
                          <img
                            src={imgVariant(cover, { w: 160, fmt: 'webp' })}
                            alt={category}
                            loading="lazy"
                            decoding="async"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: '#FAF5EE' }}>
                            <ShoppingBag className="h-6 w-6" style={{ color: '#C4A084' }} />
                          </div>
                        )}
                        {isActive && (
                          <div className="absolute inset-0 bg-[#5D4E3C]/15" />
                        )}
                      </div>
                      <span
                        className="text-[10px] sm:text-[11px] text-center leading-tight line-clamp-2 break-words"
                        style={{
                          color: isActive ? '#5D4E3C' : '#6D5A47',
                          fontWeight: isActive ? 600 : 400,
                          maxWidth: '76px',
                        }}
                      >
                        {category}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-6">
        {/* Limited Hair Collections teaser — only on default shop view (no search/filter) */}
        {!searchQuery && !selectedCategory && !selectedSubcategory && (
          <LimitedCollectionsSection />
        )}

        {/* "Neu diese Woche" — newest products strip (only on default shop view, no search/filter) */}
        {!searchQuery && !selectedCategory && !selectedSubcategory && products.length > 0 && (() => {
          const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
          let newest = products
            .filter(p => p.created_at && new Date(p.created_at).getTime() >= sevenDaysAgo)
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            .slice(0, 8);
          // Fallback: if no products in last 7 days, just show 8 most recent overall
          if (newest.length === 0) {
            newest = [...products]
              .filter(p => p.created_at)
              .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
              .slice(0, 8);
          }
          if (newest.length === 0) return null;
          return (
            <section className="mt-8 sm:mt-10 mb-6" data-testid="new-this-week-section">
              <div className="flex items-end justify-between mb-3">
                <div>
                  <p className="text-[10px] sm:text-xs tracking-[4px] uppercase mb-1" style={{ color: '#C4A084' }}>
                    Frisch eingetroffen
                  </p>
                  <h2 className="text-xl sm:text-2xl font-bold text-[#5D4E3C]" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
                    Neu diese Woche
                  </h2>
                  <div className="w-10 h-[2px] mt-1.5" style={{ backgroundColor: '#C4A084' }} />
                </div>
              </div>
              <div
                className="flex gap-3 sm:gap-4 overflow-x-auto pb-2 -mx-3 px-3 sm:-mx-4 sm:px-4"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
                data-testid="new-this-week-strip"
              >
                {newest.map((product) => (
                  <button
                    key={product.id}
                    type="button"
                    onClick={() => navigate(`/products/${product.id}`)}
                    className="flex-shrink-0 w-[150px] sm:w-[170px] bg-white border text-left group transition-shadow hover:shadow-md"
                    style={{ borderColor: '#E8DCC8' }}
                    data-testid={`new-product-${product.id}`}
                  >
                    <div className="relative w-full aspect-square overflow-hidden bg-[#FAF5EE]">
                      {product.images?.[0] ? (
                        <img
                          src={imgVariant(product.images[0], { w: 340, fmt: 'webp' })}
                          alt={product.name}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ShoppingBag className="h-6 w-6" style={{ color: '#C4A084' }} />
                        </div>
                      )}
                      <span
                        className="absolute top-2 left-2 text-[9px] tracking-[2px] uppercase px-1.5 py-0.5 font-semibold"
                        style={{ backgroundColor: '#5D4E3C', color: '#F5EDE4' }}
                      >
                        Neu
                      </span>
                    </div>
                    <div className="p-2">
                      <p className="text-xs sm:text-sm font-medium text-[#3E2723] line-clamp-2 leading-tight min-h-[2.2em]">
                        {product.name}
                      </p>
                      <p className="text-sm font-bold mt-1" style={{ color: '#5D4E3C' }}>
                        {formatEUR(Number(product.price))}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </section>
          );
        })()}

        {/* Mobile: Filter/Categories trigger button */}
        <button
          type="button"
          onClick={() => setSidebarOpen(true)}
          className="lg:hidden mb-4 w-full flex items-center justify-between px-4 py-3 bg-white border text-sm text-[#5D4E3C]"
          style={{ borderColor: '#E8DCC8' }}
          data-testid="open-shop-sidebar"
        >
          <span className="flex items-center gap-2">
            <Filter className="h-4 w-4" style={{ color: '#C4A084' }} />
            <span className="font-medium">Kategorien & Filter</span>
          </span>
          <ChevronRight className="h-4 w-4 text-[#8B7355]" />
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
          {/* Desktop sidebar */}
          <aside className="hidden lg:block" data-testid="shop-sidebar-desktop">
            <ShopSidebar
              categoryGroups={categoryGroups}
              expanded={expandedGroups}
              onToggleGroup={toggleGroup}
              activeSub={selectedSubcategory}
              activeCat={selectedCategory}
              onPickAll={() => { setSelectedCategory(''); setSelectedSubcategory(''); navigate('/products'); }}
              onPickCategory={(cat) => { setSelectedCategory(cat); setSelectedSubcategory(''); navigate(`/products?category=${encodeURIComponent(cat)}`); }}
              onPickSubcategory={(cat, sub) => { setSelectedCategory(cat); setSelectedSubcategory(sub); navigate(`/products?category=${encodeURIComponent(cat)}&sub=${encodeURIComponent(sub)}`); }}
              sortBy={sortBy} setSortBy={setSortBy}
              priceRange={priceRange} setPriceRange={setPriceRange}
              showOnlyAvailable={showOnlyAvailable} setShowOnlyAvailable={setShowOnlyAvailable}
            />
          </aside>

          {/* Mobile sidebar drawer */}
          {sidebarOpen && (
            <div className="lg:hidden fixed inset-0 z-50 flex" data-testid="shop-sidebar-mobile">
              <div className="absolute inset-0 bg-black/40" onClick={() => setSidebarOpen(false)} />
              <div className="relative bg-white w-[78%] max-w-[320px] h-full overflow-y-auto shadow-xl">
                <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: '#E8DCC8' }}>
                  <p className="text-sm font-semibold text-[#5D4E3C]">Kategorien & Filter</p>
                  <button onClick={() => setSidebarOpen(false)} aria-label="Schließen" className="p-1 text-[#8B7355]">
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <div className="p-3">
                  <ShopSidebar
                    categoryGroups={categoryGroups}
                    expanded={expandedGroups}
                    onToggleGroup={toggleGroup}
                    activeSub={selectedSubcategory}
                    activeCat={selectedCategory}
                    onPickAll={() => { setSelectedCategory(''); setSelectedSubcategory(''); setSidebarOpen(false); navigate('/products'); }}
                    onPickCategory={(cat) => { setSelectedCategory(cat); setSelectedSubcategory(''); setSidebarOpen(false); navigate(`/products?category=${encodeURIComponent(cat)}`); }}
                    onPickSubcategory={(cat, sub) => { setSelectedCategory(cat); setSelectedSubcategory(sub); setSidebarOpen(false); navigate(`/products?category=${encodeURIComponent(cat)}&sub=${encodeURIComponent(sub)}`); }}
                    sortBy={sortBy} setSortBy={setSortBy}
                    priceRange={priceRange} setPriceRange={setPriceRange}
                    showOnlyAvailable={showOnlyAvailable} setShowOnlyAvailable={setShowOnlyAvailable}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Right: mixed product grid */}
          <section>
            {/* Active-filter chips */}
            {(selectedCategory || selectedSubcategory) && (
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <span className="text-xs uppercase tracking-wider text-[#8B7355]">Filter:</span>
                {selectedCategory && (
                  <button
                    type="button"
                    onClick={() => { setSelectedCategory(''); setSelectedSubcategory(''); navigate('/products'); }}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs"
                    style={{ backgroundColor: '#FAF5EE', color: '#5D4E3C', border: '1px solid #C4A084' }}
                  >
                    {selectedCategory} <X className="h-3 w-3" />
                  </button>
                )}
                {selectedSubcategory && (
                  <button
                    type="button"
                    onClick={() => { setSelectedSubcategory(''); navigate(`/products?category=${encodeURIComponent(selectedCategory)}`); }}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs"
                    style={{ backgroundColor: '#5D4E3C', color: '#FFFFFF' }}
                  >
                    {selectedSubcategory} <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            )}

            {(() => {
              // Apply category/subcategory filter, then mix products in shuffled order.
              let pool = products;
              if (selectedCategory) pool = pool.filter(p => p.category === selectedCategory);
              if (selectedSubcategory) pool = pool.filter(p => p.subcategory === selectedSubcategory);
              const filteredAndSorted = getFilteredAndSortedProducts(pool);
              const display = (sortBy === 'default' && !selectedSubcategory)
                ? shuffleStable(filteredAndSorted, shopShuffleSeed)
                : filteredAndSorted;
              if (display.length === 0) {
                return (
                  <div className="text-center py-16 bg-white border" style={{ borderColor: '#E8DCC8' }}>
                    <ShoppingBag className="h-14 w-14 mx-auto mb-4" style={{ color: '#C4A084' }} />
                    <p className="text-[#5D4E3C] text-lg" style={{ fontFamily: 'Georgia, serif' }}>
                      Hier gibt es noch nichts.
                    </p>
                    <p className="text-[#8B7355] text-sm mt-2">Versuch's mit einer anderen Kategorie.</p>
                  </div>
                );
              }
              return (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-5" data-testid="shop-product-grid">
                  {display.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
              );
            })()}
          </section>
        </div>
      </div>

      {/* Notepad Dialog */}
      <AddToNotepadDialog
        open={notepadDialog.open}
        onOpenChange={(open) => setNotepadDialog({ open, product: null })}
        product={notepadDialog.product}
        onAdd={handleAddToNotepadConfirm}
      />

      <style>{`
        [data-testid="category-tiles"] *::-webkit-scrollbar,
        [data-testid="shop-sidebar-mobile"] *::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

// ----- Helpers -----
function shuffleStable(arr, seed) {
  // Deterministic shuffle so the order is stable across re-renders in the same session.
  const out = [...arr];
  let s = seed || 1;
  for (let i = out.length - 1; i > 0; i--) {
    s = (s * 9301 + 49297) % 233280;
    const j = Math.floor((s / 233280) * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

// ----- Sidebar component -----
const ShopSidebar = ({
  categoryGroups, expanded, onToggleGroup, activeSub, activeCat,
  onPickAll, onPickCategory, onPickSubcategory,
  sortBy, setSortBy, priceRange, setPriceRange, showOnlyAvailable, setShowOnlyAvailable,
}) => {
  return (
    <div className="space-y-5">
      {/* Categories tree */}
      <div className="bg-white border" style={{ borderColor: '#E8DCC8' }}>
        <div className="px-3 py-2 border-b" style={{ borderColor: '#E8DCC8' }}>
          <p className="text-[10px] uppercase tracking-[3px] text-[#C4A084] font-semibold">Stöbern</p>
        </div>
        <nav className="py-1">
          <button
            type="button"
            onClick={onPickAll}
            className="w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-[#FAF5EE]"
            style={{
              color: !activeCat ? '#5D4E3C' : '#6D5A47',
              fontWeight: !activeCat ? 600 : 400,
              borderLeft: !activeCat ? '3px solid #C4A084' : '3px solid transparent',
            }}
            data-testid="shop-sidebar-all"
          >
            <span>Alle Artikel</span>
            <span className="text-xs text-[#A89A85]">{categoryGroups.reduce((s, g) => s + g.count, 0)}</span>
          </button>
          {categoryGroups.map((group) => {
            const isOpen = expanded[group.name];
            const isActive = activeCat === group.name;
            return (
              <div key={group.name}>
                <button
                  type="button"
                  onClick={() => {
                    onToggleGroup(group.name);
                    onPickCategory(group.name);
                  }}
                  className="w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-[#FAF5EE]"
                  style={{
                    color: isActive ? '#5D4E3C' : '#6D5A47',
                    fontWeight: isActive ? 600 : 400,
                    borderLeft: isActive ? '3px solid #C4A084' : '3px solid transparent',
                  }}
                  data-testid={`shop-sidebar-cat-${group.name}`}
                >
                  <span className="flex items-center gap-1.5 truncate">
                    <ChevronRight
                      className="h-3 w-3 flex-shrink-0 transition-transform"
                      style={{ transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}
                    />
                    <span className="truncate">{group.name}</span>
                  </span>
                  <span className="text-xs text-[#A89A85] flex-shrink-0">{group.count}</span>
                </button>
                {isOpen && group.subs.length > 0 && (
                  <div className="pb-1">
                    {group.subs.map((sub) => {
                      const subActive = activeSub === sub.name && activeCat === group.name;
                      return (
                        <button
                          key={sub.name}
                          type="button"
                          onClick={() => onPickSubcategory(group.name, sub.name)}
                          className="w-full text-left pl-8 pr-3 py-1.5 text-[13px] flex items-center justify-between hover:bg-[#FAF5EE]"
                          style={{
                            color: subActive ? '#5D4E3C' : '#8B7355',
                            fontWeight: subActive ? 600 : 400,
                          }}
                          data-testid={`shop-sidebar-sub-${group.name}-${sub.name}`}
                        >
                          <span className="truncate">{sub.name}</span>
                          <span className="text-[11px] text-[#A89A85]">{sub.count}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </div>

      {/* Sort + filters */}
      <div className="bg-white border p-3" style={{ borderColor: '#E8DCC8' }}>
        <p className="text-[10px] uppercase tracking-[3px] text-[#C4A084] font-semibold mb-2">Sortierung</p>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="w-full px-2 py-1.5 text-sm bg-white border focus:outline-none focus:ring-1 focus:ring-[#C4A084]"
          style={{ borderColor: '#E8DCC8', color: '#5D4E3C' }}
          data-testid="shop-sidebar-sort"
        >
          <option value="default">Stöbern (gemischt)</option>
          <option value="price_asc">Preis aufsteigend</option>
          <option value="price_desc">Preis absteigend</option>
          <option value="name_asc">Name A–Z</option>
          <option value="name_desc">Name Z–A</option>
          <option value="newest">Neueste zuerst</option>
        </select>

        <p className="text-[10px] uppercase tracking-[3px] text-[#C4A084] font-semibold mt-4 mb-1">Preis</p>
        <p className="text-xs text-[#6D5A47] mb-1">{formatEUR(priceRange.min)} – {formatEUR(priceRange.max)}</p>
        <div className="space-y-2">
          <div>
            <label className="text-[11px] text-[#8B7355]">Min</label>
            <input
              type="range" min="0" max="500" value={priceRange.min}
              onChange={(e) => setPriceRange({ ...priceRange, min: Number(e.target.value) })}
              className="w-full accent-[#C4A084]"
            />
          </div>
          <div>
            <label className="text-[11px] text-[#8B7355]">Max</label>
            <input
              type="range" min="0" max="500" value={priceRange.max}
              onChange={(e) => setPriceRange({ ...priceRange, max: Number(e.target.value) })}
              className="w-full accent-[#C4A084]"
            />
          </div>
        </div>

        <label className="flex items-center gap-2 mt-4 cursor-pointer text-sm text-[#5D4E3C]">
          <input
            type="checkbox"
            checked={showOnlyAvailable}
            onChange={(e) => setShowOnlyAvailable(e.target.checked)}
            className="w-4 h-4 accent-[#C4A084]"
            data-testid="shop-sidebar-available-only"
          />
          <span>Nur verfügbare zeigen</span>
        </label>
      </div>
    </div>
  );
};

export default Products;
